import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignal } from '@preact/signals-react';
import { toast } from 'react-toastify';
import PageForm from '../../templates/PageForm';
import SelectFilter from '../../organisms/SelectFilter';

const ISSUANCE_URL = `${process.env.REACT_APP_FUNDING_API_URL}/issuances`;
const API_KEY = process.env.REACT_APP_API_KEY || '';

const BROKERS = [
    { value: '37', label: 'NU - 37', name: 'NU' },
    { value: '36', label: 'TORO - 36', name: 'TORO' },
    { value: '38', label: 'XP - 38', name: 'XP' },
    { value: '60', label: 'ÁGORA - 60', name: 'ÁGORA' },
    { value: '51', label: 'ITAU - 51', name: 'ITAU' },
    { value: '43', label: 'BTG - 43', name: 'BTG' },
    { value: '108', label: 'MIRAE - 108', name: 'MIRAE' },
    { value: '94', label: 'NIKOS - 94', name: 'NIKOS' },
    { value: '116', label: 'INTER - 116', name: 'INTER' },
    { value: '86', label: 'GENIAL INST - 86', name: 'GENIAL' },
    { value: '35', label: 'DAYCOVAL - 35', name: 'DAYCOVAL' },
];

const BROKER_FIELD_MAPPING: Record<string, Record<string, string>> = {
    DAYCOVAL: {
        'TIPO IF': 'product',
        'VENCIMENTO': 'due_date',
        'INDEXADOR': 'index',
        'TAXA': 'tax',
        'QUANTIDADE': 'quantity',
        'PU DESAGIADO': 'pu_degassed',
    },
    XP: {
        'ATIVO': 'product',
        'DT. VENCIMENTO': 'due_date',
        'INDEXADOR': 'index',
        'TAXA PRÉ-FIXADA CLIENTE': 'tax',
        'QUANTIDADE': 'quantity',
        'PU': 'pu_degassed',
    },
    TORO: {
        'ATIVO': 'product',
        'DATA VENCIMENTO': 'due_date',
        'INDEXADOR': 'index',
        'TAXA CLIENTE': 'tax',
        'QUANTIDADE': 'quantity',
        'PU DESAGIADO': 'pu_degassed',
    },
    GENIAL: {
        'TÍTULO': 'product',
        'VENCTO': 'due_date',
        'ÍNDICE': 'index',
        'TAXA DE EMISSÃO': 'tax',
        'QTDE': 'quantity',
        'PU DESAGIADO': 'pu_degassed',
    },
    NIKOS: {
        'PAPEL': 'product',
        'VENCIMENTO': 'due_date',
        'INDICE': 'index',
        'TAXA CLIENTE': 'tax',
        'QUANTIDADE': 'quantity',
        'PU DESAGIADO': 'pu_degassed',
    },
    ÁGORA: {
        'ATIVO': 'product',
        'VCTO': 'due_date',
        'INDICE': 'index',
        'TX EMISSÃO': 'tax',
        'QTDE': 'quantity',
        'PU DESÁGIO': 'pu_degassed',
    },
    INTER: {
        'ATIVO': 'product',
        'PRAZO / VENCIMENTO': 'due_date',
        'INDEXADOR': 'index',
        'TAXA CLIENTE (EMISSÃO)': 'tax',
        'QUANTIDADE': 'quantity',
        'PU DESAGIADO': 'pu_degassed',
    },
    ITAU: {
        'PRODUTO': 'product',
        'DATA VENCIMENTO': 'due_date',
        'INDEXADOR': 'index',
        'TAXA CLIENTE': 'tax',
        'QUANTIDADE': 'quantity',
        'PU DESAGIADO': 'pu_degassed',
    },
    BTG: {
        'PRODUTO': 'product',
        'DATA VENCIMENTO': 'due_date',
        'INDEXADOR': 'index',
        'TAXA CLIENTE': 'tax',
        'QUANTIDADE': 'quantity',
        'PU DESAGIADO': 'pu_degassed',
    },
};

interface Form {
    brokerId: string;
    tableData: any[];
}

const IssuanceFormPage: React.FC = () => {
    const navigate = useNavigate();
    const form = useSignal<Form>({ brokerId: '', tableData: [] });
    const isSending = useSignal(false);
    const buttonDisabled = useSignal(true);
    const selectedBrokerName = useSignal('');
    const pastedData = useSignal('');
    const processedData = useSignal<any[]>([]);

    const processPastedData = (text: string) => {
        if (!selectedBrokerName.value) return;

        const mapping = BROKER_FIELD_MAPPING[selectedBrokerName.value];
        if (!mapping) {
            toast.error('Corretora selecionada ainda não possui mapeamento automático configurado.');
            return;
        }

        const lines = text.split('\n').filter(line => line.trim());
        if (lines.length < 2) return;

        const headers = lines[0].split('\t').map(h => h.trim().toUpperCase());
        const dataLines = lines.slice(1);

        const mapped = dataLines.map(line => {
            const values = line.split('\t').map(v => v.trim());
            const row: any = {};

            headers.forEach((header, index) => {
                const systemField = mapping[header];
                if (systemField) {
                    row[systemField] = values[index] || '';
                }
            });

            return row;
        });

        processedData.value = mapped;
        form.value = { ...form.value, tableData: mapped };
        buttonDisabled.value = mapped.length === 0 || !form.value.brokerId;
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const text = e.clipboardData.getData('text');
        pastedData.value = text;
        processPastedData(text);
    };

    const submitForm = async () => {
        if (buttonDisabled.value) {
            toast.error('Por favor, preencha todos os campos obrigatórios.');
            return;
        }

        isSending.value = true;
        try {
            const response = await fetch(ISSUANCE_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': API_KEY || ''
                },
                body: JSON.stringify({
                    brokerId: form.value.brokerId,
                    data: form.value.tableData,
                }),
            });
            if (response.ok) {
                toast.info('Emissão lançada com sucesso.');
                navigate('/funding/issuance/list');
            } else {
                const { message } = await response.json();
                toast.error(message || 'Erro ao enviar formulário.');
            }
        } catch (err) {
            toast.error('Erro de rede ao enviar formulário.');
        }
        isSending.value = false;
    };

    const cancelForm = () => navigate(-1);

    const clearData = () => {
        pastedData.value = '';
        processedData.value = [];
        form.value = { ...form.value, tableData: [] };
        buttonDisabled.value = true;
    };

    return (
        <PageForm
            pageTitle='Lançar Emissões'
            apiVersion='Funding API - v1'
            isLoading={false}
            breadcrumb={[{ label: 'Tesouraria', url: '/funding/issuance/list' }, { label: 'Lançar Emissões', url: '#' }]}
            controlSlot={(
                <div className='flex space-x-4 justify-end m-2'>
                    {processedData.value.length > 0 && (
                        <button
                            className='bg-red-500 text-white px-4 py-2 rounded hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-800'
                            onClick={clearData}
                            disabled={isSending.value}
                        >
                            Limpar Dados
                        </button>
                    )}
                    <button
                        className='text-black bg-slate-200 px-4 py-2 hover:bg-slate-400 rounded dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600'
                        onClick={cancelForm}
                        disabled={isSending.value}
                    >
                        Cancelar
                    </button>
                    <button
                        className='bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-blue-600 dark:hover:bg-blue-700'
                        onClick={submitForm}
                        disabled={isSending.value || buttonDisabled.value}
                    >
                        Enviar
                    </button>
                </div>
            )}
        >
            <div className='container mx-auto p-4 space-y-4'>
                <SelectFilter
                    id='slc-broker'
                    label='Corretora:'
                    value={form.value.brokerId}
                    onChange={(value) => {
                        const broker = BROKERS.find(b => b.value === value);
                        if (broker) {
                            selectedBrokerName.value = broker.name;
                            form.value = { ...form.value, brokerId: value };
                            if (pastedData.value) {
                                processPastedData(pastedData.value);
                            }
                        }
                    }}
                    defaultVoidOption
                    defaultLabel='Selecione a corretora...'
                    options={BROKERS}
                />

                {form.value.brokerId && processedData.value.length === 0 && (
                    <div>
                        <label className='block text-sm font-medium mb-2 dark:text-white'>
                            Cole os dados da tabela:
                        </label>
                        <textarea
                            className='w-full p-3 border border-gray-300 rounded dark:bg-slate-700 dark:border-slate-600 dark:text-white'
                            rows={8}
                            placeholder='Cole aqui a tabela copiada do Excel...'
                            onPaste={handlePaste}
                        />
                    </div>
                )}

                {processedData.value.length > 0 && (
                    <div className='overflow-x-auto'>
                        <div className='bg-green-50 border border-green-200 rounded p-3 mb-4 dark:bg-green-900 dark:border-green-700'>
                            <p className='text-green-800 font-medium dark:text-green-100'>
                                ✓ {processedData.value.length} registro(s) processado(s) com sucesso
                            </p>
                        </div>
                        <table className='min-w-full border border-gray-300 dark:border-slate-600'>
                            <thead className='bg-blue-500 text-white dark:bg-blue-700'>
                                <tr>
                                    <th className='border px-4 py-2 text-left'>Produto</th>
                                    <th className='border px-4 py-2 text-left'>Vencimento</th>
                                    <th className='border px-4 py-2 text-left'>Indexador</th>
                                    <th className='border px-4 py-2 text-left'>Taxa</th>
                                    <th className='border px-4 py-2 text-left'>Quantidade</th>
                                    <th className='border px-4 py-2 text-left'>PU Desagiado</th>
                                </tr>
                            </thead>
                            <tbody className='bg-white dark:bg-slate-800'>
                                {processedData.value.map((row, idx) => (
                                    <tr key={idx} className='hover:bg-gray-50 dark:hover:bg-slate-700'>
                                        <td className='border px-4 py-2 dark:border-slate-600 dark:text-white'>{row.product || '-'}</td>
                                        <td className='border px-4 py-2 dark:border-slate-600 dark:text-white'>{row.due_date || '-'}</td>
                                        <td className='border px-4 py-2 dark:border-slate-600 dark:text-white'>{row.index || '-'}</td>
                                        <td className='border px-4 py-2 dark:border-slate-600 dark:text-white'>{row.tax || '-'}</td>
                                        <td className='border px-4 py-2 dark:border-slate-600 dark:text-white'>{row.quantity || '-'}</td>
                                        <td className='border px-4 py-2 dark:border-slate-600 dark:text-white'>{row.pu_degassed || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </PageForm>
    );
};

export default IssuanceFormPage;
